import { useRef } from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies
import { uncurlyUuid } from '@atlassian/bitkit-analytics';
import { createPromptEditor } from '@atlassian/editor-ai-injected-editors/prompt-editor';
import { createBBEditorPluginAIOptions } from '@atlassian/editor-plugin-ai/BitbucketPrebuilt';
// eslint-disable-next-line import/extensions, import/no-unresolved
import { AIGlobalOptIn } from '@atlassian/editor-plugin-ai/dist/types/types/types';

export interface PluginOptions {
  // thumbs up or thumbs down is clicked
  onFeedbackClicked?: (feedbackContext: any) => void;
  // Triggers when the AI plugin enters a review state
  onReviewStateEntered?: () => void;
  // user finishes AI experience (modal closes and content inserted/cancelled)
  onExperienceComplete?: () => void;
}

export type EditorProvider = ReturnType<typeof createBBEditorPluginAIOptions>;

function createProvider({
  options,
  workspaceUuid,
  summarisePrDescriptionUrl,
  aiGlobalOptIn,
}: {
  options: PluginOptions | undefined;
  workspaceUuid: string;
  summarisePrDescriptionUrl?: string;
  aiGlobalOptIn: AIGlobalOptIn;
}): EditorProvider {
  const workspaceId = uncurlyUuid(workspaceUuid);

  const PromptEditor = createPromptEditor({
    linking: {
      smartLinks: {},
    },
    featureFlags: {},
  });

  return createBBEditorPluginAIOptions({
    generativeAIApiUrl: '',
    // since BBC does not have cloudIds, the workspaceUuid has to be
    // included as a header for Confluence AI to use for authentication
    getFetchCustomHeaders: () => ({
      workspaceId,
      'x-workspace-id': workspaceId,
    }),
    handleFeedbackSubmission: (feedbackContext: any) => {
      try {
        if (options?.onFeedbackClicked) {
          options.onFeedbackClicked(feedbackContext);
        }
        return Promise.resolve({ status: 'submitted' as const });
      } catch {
        return Promise.resolve({ status: 'failed' as const });
      }
    },
    summarisePrDescriptionUrl: summarisePrDescriptionUrl || undefined,
    PromptEditor,
    product: 'BITBUCKET',
    aiGlobalOptIn,
  });
}

// Hook to create AI plugin for Atlaskit Editor
export const useEditorProviderAI = ({
  options,
  workspaceUuid,
  summarisePrDescriptionUrl,
  aiGlobalOptIn,
}: {
  options?: PluginOptions;
  workspaceUuid?: string;
  summarisePrDescriptionUrl?: string;
  aiGlobalOptIn: AIGlobalOptIn;
}): EditorProvider | null => {
  const providerRef = useRef<EditorProvider | null>();

  if (!workspaceUuid) {
    return null;
  }

  providerRef.current =
    providerRef.current ??
    createProvider({
      options,
      workspaceUuid,
      summarisePrDescriptionUrl,
      aiGlobalOptIn,
    });
  return providerRef.current;
};
